import { io } from 'socket.io-client';
import { API_URL } from '../constants/api';

class SocketService {
  constructor() {
    this.socket = null;
    this.connected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.messageHandlers = new Map();
    this.typingTimeout = null;
  }

  connect(token) {
    if (this.socket && this.connected) {
      return this.socket;
    }

    if (!token) {
      console.error('❌ No token provided to connect()');
      return null;
    }

    const isDev = process.env.NODE_ENV === 'development';
    const backendUrl = API_URL;
    if (isDev) console.log('🔌 Initiating Socket.IO connection...');
    
    this.socket = io(backendUrl, {
      path: '/api/socket.io',
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 10000,
    });

    // Connection events
    this.socket.on('connect', () => {
      if (isDev) console.log('🎉 Socket.IO connected. Transport:', this.socket.io.engine.transport.name);
      this.connected = true;
      this.reconnectAttempts = 0;
      this.triggerHandler('connect', {});
    });

    this.socket.on('connected', (_data) => {});

    this.socket.on('disconnect', (reason) => {
      this.connected = false;
      if (reason === 'io server disconnect') {
        this.socket.connect();
      }
    });

    this.socket.on('connect_error', (error) => {
      if (isDev) console.error('Socket connect error:', error.message);
      this.reconnectAttempts++;
    });

    this.socket.on('reconnect', (_attempt) => {
      this.reconnectAttempts = 0;
    });

    // Chat events
    this.socket.on('new_message', (message) => {
      this.triggerHandler('new_message', message);
    });

    this.socket.on('user_joined', (data) => {
      this.triggerHandler('user_joined', data);
    });

    this.socket.on('user_left', (data) => {
      this.triggerHandler('user_left', data);
    });

    this.socket.on('user_typing', (data) => {
      this.triggerHandler('user_typing', data);
    });

    // Real-time update events
    this.socket.on('issue_comment_added', (data) => this.triggerHandler('issue_comment_added', data));
    this.socket.on('issue_completed', (data) => this.triggerHandler('issue_completed', data));
    this.socket.on('issue_assigned', (data) => this.triggerHandler('issue_assigned', data));
    this.socket.on('decision_logged', (data) => this.triggerHandler('decision_logged', data));
    this.socket.on('momentum_updated', (data) => this.triggerHandler('momentum_updated', data));

    return this.socket;
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
      this.messageHandlers.clear();
    }
  }

  // Wait for socket to be connected with timeout
  waitForConnection(timeout = 10000) {
    return new Promise((resolve, reject) => {
      if (this.connected && this.socket) {
        resolve(this.socket);
        return;
      }

      const startTime = Date.now();
      const checkInterval = setInterval(() => {
        if (this.connected && this.socket) {
          clearInterval(checkInterval);
          resolve(this.socket);
          return;
        }

        if (Date.now() - startTime > timeout) {
          clearInterval(checkInterval);
          reject(new Error(`Socket connection timeout after ${timeout}ms`));
        }
      }, 100);
    });
  }

  // Join a chat room
  async joinChat(groupType, groupId) {
    try {
      // Wait for socket to be connected
      await this.waitForConnection(10000);

      return new Promise((resolve, reject) => {
        if (!this.socket) {
          reject(new Error('Socket is null'));
          return;
        }

        this.socket.emit('join_chat', { group_type: groupType, group_id: groupId }, (response) => {
          if (response && response.error) {
            reject(new Error(response.error));
          } else {
            resolve(response);
          }
        });

        // Timeout if no response after 5 seconds
        setTimeout(() => {
          reject(new Error('Join chat timeout - no response from server'));
        }, 5000);
      });
    } catch (error) {
      console.error(`Failed to join chat (${groupType}/${groupId}):`, error.message);
      throw error;
    }
  }

  // Leave a chat room
  leaveChat(groupType, groupId) {
    return new Promise((resolve, reject) => {
      if (!this.socket) {
        reject(new Error('Socket not connected'));
        return;
      }

      this.socket.emit('leave_chat', { group_type: groupType, group_id: groupId }, (response) => {
        if (response.error) {
          console.error('Failed to leave chat:', response.error);
          reject(new Error(response.error));
        } else {
          console.log('✅ Left chat room:', response.room);
          resolve(response);
        }
      });
    });
  }

  // Send a message
  sendMessage(groupType, groupId, message, replyTo = null) {
    return new Promise((resolve, reject) => {
      if (!this.socket || !this.connected) {
        reject(new Error('Socket not connected'));
        return;
      }

      console.log(`🔌 Emitting send_message for ${groupType}/${groupId}`);

      const payload = {
        group_type: groupType,
        group_id: groupId,
        message: message,
      };
      if (replyTo && replyTo.id) {
        payload.reply_to = {
          id: replyTo.id,
          user_id: replyTo.user_id || null,
          user_name: replyTo.user_name || null,
          message: (replyTo.message || '').slice(0, 200),
        };
      }

      this.socket.emit(
        'send_message',
        payload,
        (response) => {
          console.log('📨 Send message response:', response);
          
          if (response && response.error) {
            console.error('❌ Failed to send message:', response.error);
            reject(new Error(response.error));
          } else {
            console.log('✅ Message sent:', response?.message_id);
            resolve(response);
          }
        }
      );

      // Timeout after 10 seconds
      setTimeout(() => {
        reject(new Error('Send message timeout - no response from server'));
      }, 10000);
    });
  }

  // Send typing indicator
  sendTyping(groupType, groupId, isTyping = true) {
    if (!this.socket || !this.connected) return;

    // Debounce typing events
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }

    this.socket.emit('typing', {
      group_type: groupType,
      group_id: groupId,
      is_typing: isTyping,
    });

    // Auto-clear typing after 3 seconds
    if (isTyping) {
      this.typingTimeout = setTimeout(() => {
        this.sendTyping(groupType, groupId, false);
      }, 3000);
    }
  }

  // Register event handler
  on(event, handler) {
    if (!this.messageHandlers.has(event)) {
      this.messageHandlers.set(event, []);
    }
    this.messageHandlers.get(event).push(handler);
  }

  // Unregister event handler
  off(event, handler) {
    if (this.messageHandlers.has(event)) {
      const handlers = this.messageHandlers.get(event);
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  // Trigger event handlers
  triggerHandler(event, data) {
    if (this.messageHandlers.has(event)) {
      this.messageHandlers.get(event).forEach((handler) => {
        try {
          handler(data);
        } catch (error) {
          console.error(`Error in ${event} handler:`, error);
        }
      });
    }
  }

  isConnected() {
    return this.socket?.connected === true;
  }
}

// Export singleton instance
const socketService = new SocketService();
export default socketService;
